<template>
    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            Invoices
          </h1>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            View and download your billing invoices
          </p>
        </div>
        <Link :href="route('settings.billing.index')">
          <Button variant="secondary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Billing
          </Button>
        </Link>
      </div>

      <!-- Error Message -->
      <Alert v-if="error" variant="danger">
        {{ error }}
      </Alert>

      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Invoices</p>
              <p class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ invoices.total || 0 }}
              </p>
            </div>
            <div class="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card>
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Paid</p>
              <p class="text-2xl font-bold text-green-600 dark:text-green-400">
                £{{ totalPaid.toFixed(2) }}
              </p>
            </div>
            <div class="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card>
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Outstanding</p>
              <p class="text-2xl font-bold text-red-600 dark:text-red-400">
                £{{ totalOutstanding.toFixed(2) }}
              </p>
            </div>
            <div class="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      <!-- Filters -->
      <Card>
        <div class="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div class="flex flex-col sm:flex-row gap-4">
            <!-- Status Filter -->
            <div class="w-full sm:w-48">
              <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                id="status"
                v-model="filterForm.status"
                @change="applyFilters"
                class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option v-for="status in statuses" :key="status" :value="status">
                  {{ formatStatus(status) }}
                </option>
              </select>
            </div>

            <!-- Year Filter -->
            <div class="w-full sm:w-48">
              <label for="year" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
                Year
              </label>
              <select
                id="year"
                v-model="filterForm.year"
                @change="applyFilters"
                class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">All Years</option>
                <option v-for="year in availableYears" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
            </div>
          </div>

          <!-- Clear Filters -->
          <Button
            v-if="hasActiveFilters"
            variant="secondary"
            size="sm"
            @click="clearFilters"
          >
            Clear Filters
          </Button>
        </div>
      </Card>

      <!-- Invoices Table -->
      <Card>
        <div v-if="invoices.data && invoices.data.length > 0" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-800">
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Invoice
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
              <tr
                v-for="invoice in invoices.data"
                :key="invoice.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <!-- Invoice Number -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex flex-col">
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ invoice.invoice_number }}
                    </p>
                    <p v-if="invoice.is_overdue" class="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                      Overdue
                    </p>
                  </div>
                </td>

                <!-- Date -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900 dark:text-white">
                    {{ invoice.invoice_date }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Due: {{ invoice.due_date }}
                  </div>
                </td>

                <!-- Status -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getStatusClass(invoice.status)"
                  >
                    {{ invoice.status_label }}
                  </span>
                </td>

                <!-- Amount -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-semibold text-gray-900 dark:text-white">
                    {{ invoice.formatted_total }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Tax: {{ invoice.formatted_tax }}
                  </div>
                </td>

                <!-- Actions -->
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex items-center justify-end space-x-2">
                    <!-- Pay Now Button -->
                    <Button
                      v-if="invoice.is_overdue && invoice.status === 'pending'"
                      variant="primary"
                      size="sm"
                      @click="retryPayment(invoice.id)"
                      :loading="retrying === invoice.id"
                    >
                      Pay Now
                    </Button>

                    <!-- Download Button -->
                    <a
                      :href="route('settings.billing.download-invoice', invoice.id)"
                      target="_blank"
                      class="inline-flex items-center justify-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      title="Download Invoice PDF"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </a>

                    <!-- View Details Button -->
                    <button
                      @click="viewInvoiceDetails(invoice)"
                      class="inline-flex items-center justify-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      title="View Details"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <div class="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white mb-2">
            No Invoices Found
          </h3>
          <p class="text-gray-600 dark:text-gray-400">
            {{ hasActiveFilters ? 'Try adjusting your filters' : 'You don\'t have any invoices yet' }}
          </p>
        </div>

        <!-- Pagination -->
        <div v-if="invoices.data && invoices.data.length > 0" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div class="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <!-- Results Info -->
            <div class="text-sm text-gray-700 dark:text-gray-300">
              Showing
              <span class="font-medium">{{ invoices.from }}</span>
              to
              <span class="font-medium">{{ invoices.to }}</span>
              of
              <span class="font-medium">{{ invoices.total }}</span>
              results
            </div>

            <!-- Pagination Links -->
            <div class="flex items-center space-x-2">
              <template v-for="(link, index) in invoices.links" :key="index">
                <Link
                  v-if="link.url"
                  :href="link.url"
                  :class="[
                    'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    link.active
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  ]"
                  v-html="link.label"
                />
                <span
                  v-else
                  :class="[
                    'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  ]"
                  v-html="link.label"
                />
              </template>
            </div>
          </div>
        </div>
      </Card>
    </div>

    <!-- Invoice Details Modal -->
    <Modal :show="showDetailsModal" @close="showDetailsModal = false" size="lg">
      <template #title>Invoice Details</template>

      <div v-if="selectedInvoice" class="space-y-6">
        <!-- Invoice Header -->
        <div class="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">
              {{ selectedInvoice.invoice_number }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ selectedInvoice.invoice_date }}
            </p>
          </div>
          <span
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
            :class="getStatusClass(selectedInvoice.status)"
          >
            {{ selectedInvoice.status_label }}
          </span>
        </div>

        <!-- Invoice Summary -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Subtotal</p>
            <p class="text-base font-medium text-gray-900 dark:text-white">
              {{ selectedInvoice.formatted_subtotal }}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Tax</p>
            <p class="text-base font-medium text-gray-900 dark:text-white">
              {{ selectedInvoice.formatted_tax }}
            </p>
          </div>
        </div>

        <!-- Total -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <p class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">Total</p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ selectedInvoice.formatted_total }}
            </p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" @click="showDetailsModal = false">
            Close
          </Button>
          <a
            :href="route('settings.billing.download-invoice', selectedInvoice.id)"
            target="_blank"
          >
            <Button>
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download PDF
            </Button>
          </a>
        </div>
      </div>
    </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router, Link } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Alert from '@/Components/Common/Alert.vue';
import Modal from '@/Components/Common/Modal.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

// Props
const props = defineProps({
  invoices: {
    type: Object,
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  availableYears: {
    type: Array,
    default: () => [],
  },
  statuses: {
    type: Array,
    default: () => [],
  },
  error: {
    type: String,
    default: null,
  },
});

// Composables
const { success, error: showError } = useToast();

// Refs
const retrying = ref(null);
const showDetailsModal = ref(false);
const selectedInvoice = ref(null);
const filterForm = ref({
  status: props.filters.status || '',
  year: props.filters.year || '',
});

// Computed
const hasActiveFilters = computed(() => {
  return filterForm.value.status || filterForm.value.year;
});

const totalPaid = computed(() => {
  if (!props.invoices.data) return 0;
  return props.invoices.data
    .filter(invoice => invoice.status === 'paid')
    .reduce((sum, invoice) => sum + parseFloat(invoice.total || 0), 0);
});

const totalOutstanding = computed(() => {
  if (!props.invoices.data) return 0;
  return props.invoices.data
    .filter(invoice => ['pending', 'failed'].includes(invoice.status))
    .reduce((sum, invoice) => sum + parseFloat(invoice.total || 0), 0);
});

// Methods
const getStatusClass = (status) => {
  const classes = {
    paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    voided: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    refunded: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    partially_refunded: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  };
  return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
};

const formatStatus = (status) => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const applyFilters = () => {
  router.get(
    route('settings.billing.invoices'),
    {
      status: filterForm.value.status,
      year: filterForm.value.year,
    },
    {
      preserveState: true,
      preserveScroll: true,
    }
  );
};

const clearFilters = () => {
  filterForm.value = {
    status: '',
    year: '',
  };
  applyFilters();
};

const viewInvoiceDetails = (invoice) => {
  selectedInvoice.value = invoice;
  showDetailsModal.value = true;
};

const retryPayment = async (invoiceId) => {
  retrying.value = invoiceId;

  try {
    const response = await axios.post(route('api.settings.billing.retry-payment'));

    if (response.data.success) {
      success('Payment processed successfully');
      router.reload({ only: ['invoices'] });
    } else {
      showError(response.data.message || 'Payment failed');
    }
  } catch (err) {
    showError('Failed to process payment');
  } finally {
    retrying.value = null;
  }
};
</script>
